import { Cards, Eraser } from 'reicon-react';
import { useState, type ReactElement } from 'react';
import { acaoBloqueavel, atributosDeBloqueio, type MotivoBloqueio } from '@/lib/bloqueio';
import { cn } from '@/lib/utils';
import { useVendaStore } from '../../stores/vendaStore';
import { AVISO_TEF_IRREVERSIVEL } from '../../stores/slices/pagamentoSlice';
import type { FormaPagamento } from '../../domain/pagamento/formaPagamento';
import { pagamentoVivo } from '../../domain/pagamento/saldoPagamento';
import { ehFormaDeValeDevolucao } from '../../domain/pagamento/valeDevolucao';
import { ControleDescontoCapa } from './ControleDescontoCapa';
import { EntradaPagamento } from './EntradaPagamento';
import { ListaPagamentosAplicados } from './ListaPagamentosAplicados';
import { ModalValeDevolucao } from './ModalValeDevolucao';
import {
  AVISO_PAGAMENTO_DO_DOCUMENTO,
  CHAMADA_VALOR_JA_RECEBIDO,
  DESTAQUE_NFCE_SAI_SEM_O_VALOR,
} from './avisosPagamentoDoDocumento';
import {
  AVISO_DESASSOCIACAO_MANUAL,
  CHAMADA_PIX_NAO_E_CANCELADO,
  DESTAQUE_PIX_SEGUE_NO_BANCO,
} from './pix/avisosPix';
import { DialogoConfirmacaoDestrutiva } from './DialogoConfirmacaoDestrutiva';
import {
  SeletorCondicaoPagamento,
  SeletorFormaPagamento,
  type OrigemSelecao,
} from './SeletorCondicaoForma';

/**
 * "Limpar" — descarta condição, desconto de capa e vales da venda e risca as
 * formas que ainda valem (pedido do usuário, 2026-09-04; feature 018, AD-282).
 *
 * As formas **não somem da lista**: ficam riscadas, como na remoção individual
 * (AD-163), e as que já estavam riscadas — o TEF estornado entre elas —
 * continuam como estavam. O operador vê tudo o que aconteceu com o pagamento da
 * venda, e a rastreabilidade no `Log` segue por forma.
 *
 * **Existe porque a condição de pagamento passou a congelar o carrinho.** Sem
 * um caminho de volta, escolher a condição por engano deixaria o operador preso:
 * a grid recusaria toda edição e não haveria gesto capaz de desfazer a escolha —
 * o combobox só troca de condição, nunca a desmarca. A frase que o carrinho
 * mostra ao recusar uma edição (`AVISO_CARRINHO_BLOQUEADO`) aponta para este
 * botão pelo nome.
 *
 * Mora no cabeçalho, e não junto da lista de pagamentos, porque a lista pode
 * estar vazia — o congelamento começa na condição, antes da primeira forma.
 *
 * Bloqueio explicativo, nunca `disabled` (AD-143), em dois casos: nada a limpar,
 * e **cartão aprovado no TEF** (I6 — a transação vive no terminal físico e o
 * cancelamento acontece lá, antes). A recusa de verdade mora em
 * `descartarPagamento`; aqui ela é antecipada para o operador ler o motivo antes
 * de tentar.
 *
 * **PIX não bloqueia mais, pergunta** (AD-161, item 2 do usuário): descartar o
 * pagamento com uma cobrança PIX na venda passa pela mesma confirmação da
 * remoção individual. A regra é a de sempre — o Checkout não cancela cobrança
 * PIX —, e travar o botão nunca desfez nada; só deixava o operador sem saída.
 *
 * **Pagamento vindo do documento também pergunta** (AD-169). É a saída oficial
 * de uma venda retomada já paga: a forma aprovada congela a grid, e este botão
 * é o caminho — nomeado pelo próprio aviso de bloqueio do carrinho — para
 * voltar a editar os itens. Mas o valor **já foi recebido** e está gravado no
 * documento dentro do ERP: descartá-lo sem perguntar deixaria a NFCe sair sem
 * o pagamento que o cliente fez, num gesto que o operador daria só para
 * corrigir um item. Não bloqueia, pelo mesmo motivo do PIX — travar aqui
 * fecharia a única saída e não desfaria nada.
 */
function BotaoLimparPagamento(): ReactElement {
  const condicaoSelecionada = useVendaStore((estado) => estado.condicaoSelecionada);
  const descontoCapa = useVendaStore((estado) => estado.descontoCapa);
  const pagamentos = useVendaStore((estado) => estado.pagamentos);
  const descartarPagamento = useVendaStore((estado) => estado.descartarPagamento);
  const [confirmando, setConfirmando] = useState<'pix' | 'documento' | null>(null);

  const temTefAprovado = pagamentos.some(
    (pagamento) => pagamento.integracao === 'TEF' && pagamento.status === 'APROVADO',
  );
  // Só PIX vivo: o "Limpar" não mexe numa cobrança já riscada, então perguntar
  // por ela seria pedir confirmação de um gesto que nada tem a ver com ela.
  const temPix = pagamentos.some(
    (pagamento) => pagamento.integracao === 'PIX_DINAMICO' && pagamentoVivo(pagamento),
  );
  // `importarFormasDePagamento` grava `integracao: 'NENHUMA'` em tudo o que vem
  // do documento, então nenhuma forma importada é `PIX_DINAMICO` e as duas
  // confirmações nunca competem pelo mesmo pagamento. A ordem abaixo ainda dá
  // precedência a esta: o valor já recebido é o aviso mais grave dos dois.
  const temPagamentoDoDocumento = pagamentos.some(
    (pagamento) => pagamento.veioDeDocumento && pagamento.status === 'APROVADO',
  );
  // Forma riscada sozinha não habilita o botão (FR-006): o "Limpar" só enxerga o
  // que ainda vale, e o que ficou riscado permanece na lista de qualquer jeito.
  const vazio =
    condicaoSelecionada === null && !pagamentos.some(pagamentoVivo) && descontoCapa === null;

  // A frase do slice, e não uma cópia: desde a feature 010 ela aponta o
  // estorno pelo botão Remover, e duas redações divergiriam na próxima mudança.
  const bloqueio: MotivoBloqueio = temTefAprovado
    ? AVISO_TEF_IRREVERSIVEL
    : vazio
      ? 'Não há condição, desconto ou forma de pagamento nesta venda para limpar.'
      : null;

  return (
    <>
      <button
        type="button"
        className="flex shrink-0 items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1.5 text-sm font-semibold text-foreground hover:bg-secondary-hover aria-disabled:cursor-not-allowed aria-disabled:opacity-50 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
        data-testid="limpar-pagamento"
        {...atributosDeBloqueio(bloqueio)}
        onClick={acaoBloqueavel(bloqueio, () => {
          if (temPagamentoDoDocumento) {
            setConfirmando('documento');
            return;
          }
          if (temPix) {
            setConfirmando('pix');
            return;
          }
          descartarPagamento();
        })}
      >
        <Eraser className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
        Limpar
      </button>

      {confirmando === 'documento' && (
        <DialogoConfirmacaoDestrutiva
          testId="confirmar-limpeza-documento"
          titulo="Limpar o pagamento vindo do documento?"
          subtitulo="Esta venda foi retomada já paga"
          chamada={CHAMADA_VALOR_JA_RECEBIDO}
          explicacao={AVISO_PAGAMENTO_DO_DOCUMENTO}
          destaque={DESTAQUE_NFCE_SAI_SEM_O_VALOR}
          rotuloConfirmar="Limpar mesmo assim"
          onConfirmar={() => {
            descartarPagamento();
            setConfirmando(null);
          }}
          onCancelar={() => {
            setConfirmando(null);
          }}
        />
      )}

      {confirmando === 'pix' && (
        <DialogoConfirmacaoDestrutiva
          testId="confirmar-limpeza-pix"
          titulo="Limpar o pagamento com PIX gerado?"
          subtitulo="A cobrança já foi gerada no banco"
          chamada={CHAMADA_PIX_NAO_E_CANCELADO}
          explicacao={AVISO_DESASSOCIACAO_MANUAL}
          destaque={DESTAQUE_PIX_SEGUE_NO_BANCO}
          rotuloConfirmar="Limpar mesmo assim"
          onConfirmar={() => {
            descartarPagamento();
            setConfirmando(null);
          }}
          onCancelar={() => {
            setConfirmando(null);
          }}
        />
      )}
    </>
  );
}

export interface ConfiguracaoPagamentoProps {
  /**
   * A coluna interna disputa a altura do pai (desktop) ou cresce com o conteúdo
   * (mobile)?
   *
   * É a **única** diferença entre os dois layouts, e é de apresentação pura: no
   * cartão do desktop a lista de pagamentos aplicados é o bloco que absorve a
   * falta de espaço e ganha barra por dentro; na etapa 2 do wizard quem rola é a
   * página inteira, e prender a lista numa altura fixa criaria uma segunda barra
   * dentro de uma tela que já rola.
   *
   * Nenhum comportamento de pagamento muda com isto (`FR-009`): as mesmas
   * formas, as mesmas integrações, o mesmo rascunho de forma.
   */
  readonly alturaFixa?: boolean;
}

/**
 * O miolo do pagamento (008) — cabeçalho, condição, desconto de capa, forma,
 * valor recebido e a lista de formas aplicadas.
 *
 * **Extraído de `PainelPagamentoETotais` pela feature 007** para que a etapa 2
 * do wizard mobile monte exatamente esta composição, e não uma cópia dela. O
 * que sobrou no painel é a moldura do desktop (a coluna de 392px, a faixa de
 * atalhos F6–F9 e o rodapé com total + finalizar); tudo o que decide pagamento
 * está aqui, num arquivo só, para os dois layouts (`SC-001`).
 *
 * O rascunho de forma mora aqui pelo mesmo motivo de sempre: quem compõe o
 * seletor de forma e o campo de valor é quem segura o valor que os dois leem.
 */
export function ConfiguracaoPagamento({
  alturaFixa = false,
}: ConfiguracaoPagamentoProps = {}): ReactElement {
  /**
   * Forma escolhida para a **próxima** inserção — rascunho de UI, não estado de
   * venda.
   *
   * Mora aqui, e não no `pagamentoSlice`, porque o contrato do slice
   * (`specs/008-pagamento-geral/contracts/pagamento-domain-api.md` §2) não tem
   * campo de "forma corrente": o que a venda guarda é a lista de pagamentos
   * **aplicados**. Guardá-la no slice criaria estado sem nenhuma action que o
   * limpasse — sobreviveria a `limparPagamentos()` e reapareceria na venda
   * seguinte apontando para uma forma de outra condição.
   */
  const [formaSelecionada, setFormaSelecionada] = useState<FormaPagamento | null>(null);
  const [valeAberto, setValeAberto] = useState(false);

  /**
   * Pedidos de foco no campo "Valor recebido" — um contador, e não um booleano,
   * pelo mesmo motivo de `focoVendaStore`: duas escolhas seguidas da mesma forma
   * precisam levar o foco ao campo as duas vezes.
   *
   * Sobe quando a forma é escolhida **por clique ou toque** (pedido do usuário,
   * 2026-10-07): o gesto seguinte é informar o valor, e o campo já chega com o
   * faltante preenchido e selecionado. Pela seta do teclado não sobe — ela
   * percorre várias formas no ritmo do teclado, e puxar o foco a cada tecla
   * tiraria o operador do combobox no meio do passeio. Vale devolução também
   * não: o clique abre o modal do ticket e o campo não aceita valor para ela.
   *
   * Local, e não no `focoVendaStore`, porque quem pede (este cartão) e quem foca
   * (`EntradaPagamento`) já têm pai comum.
   */
  const [pedidosDeFocoNoValor, setPedidosDeFocoNoValor] = useState(0);

  /**
   * Trocar a condição **limpa a forma escolhida** (pedido do usuário,
   * 2026-09-04).
   *
   * As formas pertencem à condição: mantida a escolha anterior, o combobox
   * exibiria o nome de uma forma que a condição nova não oferece, e
   * `EntradaPagamento` aceitaria um valor para ela — `aplicarPagamento` recusaria
   * só no clique, com a frase de forma fora da condição. Zerar aqui faz o
   * controle voltar a "Selecione a forma", que é o estado verdadeiro.
   *
   * Comparação do valor anterior durante o render, não `useEffect`: o reset
   * acontece **antes** da pintura, sem um quadro intermediário exibindo a forma
   * antiga sob a condição nova (padrão de `abertoAnterior` em
   * `ModalValeDevolucao`).
   */
  const codigoCondicao = useVendaStore((estado) => estado.condicaoSelecionada?.codigo ?? null);
  const [codigoCondicaoAnterior, setCodigoCondicaoAnterior] = useState(codigoCondicao);
  if (codigoCondicao !== codigoCondicaoAnterior) {
    setCodigoCondicaoAnterior(codigoCondicao);
    setFormaSelecionada(null);
    setValeAberto(false);
  }

  /**
   * A forma que o **atalho de cenário** (F6–F9) acabou de lançar passa a ser a
   * forma exibida (correção do usuário, 2026-10-07).
   *
   * O atalho fala só com o store: seleciona a condição e aplica a forma, sem
   * passar por `escolherForma`. A condição aparecia preenchida e o campo da forma
   * ficava em "Selecione a forma" — e o reset acima, que roda quando o atalho
   * troca a condição, ainda apagava qualquer escolha anterior. O pagamento
   * entrava na lista e o operador não via de onde ele tinha saído.
   *
   * Só vale **durante um acionamento** (`acionamentoEmAndamento`): é o que
   * separa o atalho de um pagamento importado de DAV/NFCe, que também entra na
   * lista sem passar pelo combobox e **não** deve mexer no rascunho. Fica depois
   * do reset de propósito: se condição e pagamento chegarem no mesmo render, a
   * forma do atalho vence. Comparação durante o render, como no reset acima.
   */
  const acionamentoEmAndamento = useVendaStore((estado) => estado.acionamentoEmAndamento);
  const formaDoUltimoPagamento = useVendaStore((estado) => {
    const vivo = estado.pagamentos.findLast(pagamentoVivo);
    return vivo === undefined
      ? null
      : (estado.condicaoSelecionada?.formas.find((forma) => forma.codigo === vivo.formaCodigo) ??
          null);
  });
  const [formaDoUltimoPagamentoAnterior, setFormaDoUltimoPagamentoAnterior] =
    useState(formaDoUltimoPagamento);
  if (formaDoUltimoPagamento !== formaDoUltimoPagamentoAnterior) {
    setFormaDoUltimoPagamentoAnterior(formaDoUltimoPagamento);
    if (acionamentoEmAndamento && formaDoUltimoPagamento !== null) {
      setFormaSelecionada(formaDoUltimoPagamento);
    }
  }

  /**
   * Escolher a forma de vale devolução **por clique do mouse** abre o modal do
   * ticket; qualquer outra forma, ou a mesma escolhida pela seta do teclado, só
   * vira o rascunho da próxima inserção.
   *
   * **A origem decide, não a forma** (correção do usuário, 2026-09-04). Antes o
   * modal abria sempre que o vale virava `formaSelecionada`, inclusive ao
   * percorrer o combobox com as setas — e a seta existe justamente para passear
   * pelas opções no ritmo do teclado, sem confirmar nenhuma.
   */
  function escolherForma(forma: FormaPagamento, origem: OrigemSelecao): void {
    setFormaSelecionada(forma);
    if (origem !== 'mouse') {
      return;
    }
    if (ehFormaDeValeDevolucao(forma)) {
      setValeAberto(true);
      return;
    }
    setPedidosDeFocoNoValor((atual) => atual + 1);
  }

  const formaDoVale =
    valeAberto && formaSelecionada !== null && ehFormaDeValeDevolucao(formaSelecionada)
      ? formaSelecionada
      : null;

  return (
    <>
      {/* Nó `y3cr1` "Cabeçalho pagamento": ícone `wallet-cards` de 20px + título
          Inter 18/600.

          A pílula "Seguro" (cadeado + rótulo) que o desenho põe à direita foi
          **removida** a pedido do usuário (2026-09-04): ela não descreve nenhum
          estado do sistema — não há um modo inseguro do qual distinguir esta
          tela. A faixa que ela ocupava passou a receber o botão "Limpar", que
          é a saída do congelamento descrito acima. */}
      <header className="flex h-9 shrink-0 items-center justify-between gap-[10px]">
        <div className="flex min-w-0 items-center gap-[10px]">
          <Cards className="size-5 shrink-0 text-foreground" aria-hidden="true" />
          <h2 className="text-[18px] font-semibold text-foreground">Pagamento</h2>
        </div>
        <BotaoLimparPagamento />
      </header>

      {/* No desktop esta coluna **não rola**: quem rola é a lista de pagamentos
          aplicados, por dentro (pedido do usuário, 2026-09-04). O cartão tem
          altura fixa (a da tela) e a lista é a única parte que cresce sem
          limite — e é também o único bloco daqui marcado com `min-h-0`, então é
          ela que o flex escolhe para absorver a falta de espaço; condição,
          desconto e campo de valor param no tamanho do próprio conteúdo.

          A rolagem declarada aqui é a **rede de segurança** para o caso em que
          nem a lista zerada basta (tela baixa demais para os blocos fixos): sem
          ela, o bloco de total e o botão de finalizar sairiam do cartão — e o
          botão de finalizar é justamente o que não pode sumir.

          `overflow-x-hidden` é obrigatório junto do `overflow-y-auto`, não
          decoração: pelo CSS, `overflow-x: visible` combinado com um
          `overflow-y` não-visível é **computado como `auto`**, então declarar só
          o eixo vertical cria uma barra horizontal na primeira vez que qualquer
          filho estourar 1px. Foi o que aconteceu em 2026-09-04 (scroll lateral
          no cartão de pagamento).

          O corte horizontal também comia o anel de `focus-visible` dos
          comboboxes, que se desenha 3px **para fora** da borda. `-mx-1 px-1`
          devolve essa folga por dentro.

          `overflow-clip-margin` seria o recurso natural aqui (como no
          colapsável do card de cliente, AD-134) e **não funciona neste caso**:
          pelo CSS, `overflow-x: clip` ao lado de um `overflow-y` que rola tem
          valor usado `hidden`, e a margem de clipe só vale para `clip` de
          verdade — verificado no navegador, o computado volta `hidden`.

          `gap-xs`, não `gap-base`: no Pencil os blocos do cartão distam ~8px
          (`oGiPa` y=104 → `Jup0R` y=178 → `uZUQX` y=274 → `J3Y1L` y=350,
          descontadas as alturas).

          No mobile (`alturaFixa: false`) nada disso vale: a etapa 2 do wizard
          rola inteira, então a coluna cresce com o conteúdo e não abre uma
          segunda barra dentro da primeira. */}
      <div
        className={cn(
          '-mx-1 flex flex-col gap-xs px-1',
          alturaFixa && 'min-h-0 flex-1 overflow-x-hidden overflow-y-auto',
        )}
        data-testid="configuracao-pagamento"
      >
        <SeletorCondicaoPagamento />
        <ControleDescontoCapa />
        <SeletorFormaPagamento
          formaSelecionada={formaSelecionada}
          onSelecionarForma={escolherForma}
        />
        <EntradaPagamento forma={formaSelecionada} pedidosDeFoco={pedidosDeFocoNoValor} />
        <ListaPagamentosAplicados />

        {/* O modal do vale abre a partir da **escolha da forma**, não de um
            botão na lista: o vale devolução é a própria forma de pagamento
            (`FpgUtiCar = 'VDV'`), e o "valor recebido" dela é o valor do
            ticket, que só o ERP conhece. Montado aqui porque é aqui que mora a
            forma escolhida. */}
        {formaDoVale !== null && (
          <ModalValeDevolucao
            aberto
            forma={formaDoVale}
            onFechar={() => {
              setFormaSelecionada(null);
            }}
          />
        )}
      </div>
    </>
  );
}
